// LoggedInArtist.jsx
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  Play,
  LogOut,
  MapPin,
  Search,
  Menu,
  X,
  BarChart3,
  Bell,
} from "lucide-react";
import Sidebar from "../components/ui/Sidebar";
import { toast, ToastContainer } from "react-toastify";
import { API_ENDPOINTS } from "../lib/config";
import { fetchWithAuth } from "../lib/authService";
import "react-toastify/dist/ReactToastify.css";
import "../styles/Login.css";
import Modal from "../components/Modal";
import AddEventFormFields from "./AddEventFormFields";
import MobileNBottomNav from "../components/ui/MobileBottomNav";
import YourSubscriptionModal from "../components/YourSubscriptionModal";
import SessionStatsModal from "../components/SessionStatsModal";

// Add CSS for shaking animation
const shakeKeyframes = `
@keyframes shake {
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-2px); }
  20%, 40%, 60%, 80% { transform: translateX(2px); }
}

.shake-animation {
  animation: shake 0.82s cubic-bezier(.36,.07,.19,.97) both infinite;
}
`;

// Add style tag to document head
const styleSheet = document.createElement("style");
styleSheet.innerText = shakeKeyframes;
document.head.appendChild(styleSheet);

export default function LoggedInArtist() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState({});
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [liveId, setLiveId] = useState(null);
  const [sidebarPage, setSidebarPage] = useState("home");
  const [activeFilter, setActiveFilter] = useState("all");
  const [pendingCounts, setPendingCounts] = useState({});
  const [showAddEventModal, setShowAddEventModal] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [showLiveConfirm, setShowLiveConfirm] = useState(false);
  const [showEndLiveConfirm, setShowEndLiveConfirm] = useState(false);
  const [selectedEventForLive, setSelectedEventForLive] = useState(null);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [extensionTime, setExtensionTime] = useState(15);
  const [extensionUnit, setExtensionUnit] = useState("minutes");
  const [subscription, setSubscription] = useState(null);
  const [selectedVenueForStats, setSelectedVenueForStats] = useState(null);

  // Pagination state
  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(10);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Time options for the extend modal
  const timeOptions = [
    { value: 15, unit: "minutes", label: "15 mins" },
    { value: 30, unit: "minutes", label: "30 mins" },
    { value: 1, unit: "hours", label: "1 hour" },
    { value: 2, unit: "hours", label: "2 hours" },
    { value: 3, unit: "hours", label: "3 hours" },
    { value: 4, unit: "hours", label: "4 hours" },
  ];

  // Fetch events with pagination
  const fetchEvents = async (reset = false) => {
    const token = localStorage.getItem("tringAuthToken");
    if (!token) {
      navigate("/");
      return;
    }
    const isRegistered = localStorage.getItem("isUserRegistered");
    if (isRegistered === "false") {
      navigate("/artistsignup");
      return;
    }

    const currentSkip = reset ? 0 : skip;

    try {
      const url = `${API_ENDPOINTS.EVENT_PERFORMER}?skip=${currentSkip}&limit=${limit}`;

      const evRes = await fetchWithAuth(url);
      if (!evRes.ok) throw new Error(`HTTP error! status: ${evRes.status}`);
      const evData = await evRes.json();

      const eventsArray = Array.isArray(evData) ? evData : [];

      if (reset) {
        setEvents((prev) => {
          const merged = new Map(prev.map((e) => [e.id, e]));
          eventsArray.forEach((e) =>
            merged.set(e.id, { ...merged.get(e.id), ...e })
          );
          const mergedArr = Array.from(merged.values());
          setSkip(mergedArr.length); // ✅ skip = total events count
          return mergedArr;
        });
      } else {
        setEvents((prev) => {
          const merged = new Map(prev.map((e) => [e.id, e]));
          eventsArray.forEach((e) =>
            merged.set(e.id, { ...merged.get(e.id), ...e })
          );
          const mergedArr = Array.from(merged.values());
          setSkip(mergedArr.length); // ✅ skip = total events count
          return mergedArr;
        });
      }

      setHasMore(eventsArray.length === limit);
    } catch (err) {
      console.error("Error fetching events:", err);
      setHasMore(false);
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  };

  const fetchMoreEvents = () => {
    if (hasMore && !isLoadingMore) {
      setIsLoadingMore(true);
      fetchEvents(false);
    }
  };

  // Fetch profile & events
  useEffect(() => {

    const fetchData = async () => {
      try {
        const isRegisteredStored = localStorage.getItem("isUserRegistered");
        if (isRegisteredStored === "false") {
          navigate("/artistsignup");
          return;
        }

        const profRes = await fetchWithAuth(API_ENDPOINTS.PARTNER);
        if (!profRes.ok) {
          navigate("/");
          return;
        }
        const prof = await profRes.json();
        if (!prof?.display_name || !prof?.category) {
          localStorage.setItem("isUserRegistered", "false");
          navigate("/artistsignup");
          return;
        }
        localStorage.setItem("email", prof.email || "");
        setProfile(prof);

        // Fetch Paytm subscription status
        let subData = null;
        try {
          const subGet = await fetchWithAuth(API_ENDPOINTS.PAYTM_SUBSCRIPTION, {
            method: "GET",
          });
          if (subGet.ok) {
            subData = await subGet.json();
          }
        } catch (e) {
          console.error("Subscription fetch failed:", e);
        }
        setSubscription(subData);
        localStorage.setItem("isUserRegistered", "true");
      } catch (err) {
        console.error("Error fetching profile:", err);
      }
    };

    const fetchEventsInitial = async () => {
      setLoading(true);
      await fetchEvents(true);
    };

    // Fetch pending request counts for active/approved venues
    const fetchPendingCountsForVenue = async (venueId) => {
      try {
        const [reqRes, appRes, shoutRes] = await Promise.allSettled([
          fetchWithAuth(API_ENDPOINTS.VENUE_QUEUE(venueId)),
          fetchWithAuth(API_ENDPOINTS.VENUE_APPROVED(venueId)),
          fetchWithAuth(API_ENDPOINTS.SHOUT_OUT_USERS_COUNT(venueId)),
        ]);

        let reqCount = 0;
        if (reqRes.status === "fulfilled" && reqRes.value.ok) {
          const data = await reqRes.value.json();
          reqCount = Array.isArray(data) ? data.length : 0;
        }

        let appCount = 0;
        if (appRes.status === "fulfilled" && appRes.value.ok) {
          const data = await appRes.value.json();
          appCount = Array.isArray(data) ? data.length : 0;
        }

        let shoutCount = 0;
        if (shoutRes.status === "fulfilled" && shoutRes.value.ok) {
          const data = await shoutRes.value.json();
          shoutCount =
            typeof data?.count === "number"
              ? data.count
              : Array.isArray(data)
                ? data.length
                : 0;
        }

        const total = reqCount + appCount + shoutCount;
        return { total, requests: reqCount, accepted: appCount, shoutouts: shoutCount };
      } catch (e) {
        console.error("Error fetching pending count for venue", venueId, e);
        return { total: 0, requests: 0, accepted: 0, shoutouts: 0 };
      }
    };

    const updatePendingCounts = async (venueList) => {
      const activeVenues = (venueList || []).filter(
        (e) => (e.status === "active" || e.status === "approved") && e.venueId,
      );
      if (activeVenues.length === 0) return;

      const newCounts = {};
      await Promise.all(
        activeVenues.map(async (v) => {
          const counts = await fetchPendingCountsForVenue(v.venueId);
          newCounts[v.venueId] = counts;
        }),
      );
      setPendingCounts((prev) => ({ ...prev, ...newCounts }));
    };

    fetchData();
    fetchEventsInitial();

    const interval = setInterval(() => {
      fetchEvents(true);
    }, 3000);

    // Poll pending counts every 5 seconds
    const pendingInterval = setInterval(() => {
      setEvents((currentEvents) => {
        updatePendingCounts(currentEvents);
        return currentEvents;
      });
    }, 5000);

    // ✅ Clean up interval on unmount
    return () => {
      clearInterval(interval);
      clearInterval(pendingInterval);
    };
  }, []);

  // Add this useEffect for search and filter changes
  useEffect(() => {
    setSkip(0);
    setHasMore(true);
    fetchEvents(true);
  }, [searchTerm, activeFilter]); // Add searchTerm and activeFilter as dependencies

  const fetchData = async () => {
    const token = localStorage.getItem("tringAuthToken");
    if (!token) {
      navigate("/");
      return;
    }
    try {
      const profRes = await fetchWithAuth(API_ENDPOINTS.PARTNER);
      const prof = await profRes.json();
      setProfile(prof);

      // Use the new fetchEvents function for events
      await fetchEvents(true);
    } catch (err) {
      console.error("Error fetching data:", err);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  // Format date
  const formatDate = (iso) =>
    new Date(iso).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  // Filter & sort events
  const displayed = useMemo(() => {
    let filtered = events;
    // Apply filter based on activeFilter
    if (activeFilter !== "all") {
      filtered = events.filter((event) => {
        if (activeFilter === "live") return event.status === "active";
        if (activeFilter === "upcoming") return event.status === "approved";
        if (activeFilter === "ended") return event.status === "finished";
        return true;
      });
    }

    // Apply search filter
    filtered = filtered.filter((v) => {
      const hay = (
        v.name +
        " " +
        (v.venue?.address?.formatted_address || "")
      ).toLowerCase();
      return hay.includes(searchTerm.toLowerCase());
    });

    return filtered.sort((a, b) => {
      // Always prioritize liveId first
      if (liveId === a.venueId) return -1;
      if (liveId === b.venueId) return 1;

      // Custom status order
      const getStatusOrder = (status) => {
        if (status === "active" || status === "paused") return 0;
        if (status === "approved") return 1;
        return 2; // finished
      };

      const statusA = getStatusOrder(a.status);
      const statusB = getStatusOrder(b.status);

      // Different statuses - maintain Live > Upcoming > Ended order
      if (statusA !== statusB) return statusA - statusB;

      // Same status - apply custom sorting within each category
      if (a.status === "active" || a.status === "paused") {
        // For live events - newest started first
        return new Date(b.startAt) - new Date(a.startAt);
      } else if (a.status === "approved") {
        // For upcoming - farthest in future first
        return new Date(b.startAt) - new Date(a.startAt);
      } else {
        // For ended - oldest first (longest ago at bottom)
        return new Date(b.startAt) - new Date(a.startAt);
      }
    });
  }, [events, liveId, searchTerm, activeFilter]);

  // Toggle live status with switch
  const handleLiveToggle = (venueId) => {
    const event = events.find((v) => v.venueId === venueId);
    const activeEvent = events.find(
      (v) => v.status === "active" || v.status === "paused"
    );

    if (event.status === "active") {
      setSelectedEventForLive(venueId);
      setShowEndLiveConfirm(true);
    } else if (
      (event.status === "approved" || event.status === "paused") &&
      (!activeEvent || activeEvent.venueId === venueId)
    ) {
      setSelectedEventForLive(venueId);
      setShowLiveConfirm(true);
    } else {
      toast.error(
        "Please finish the current live event before going live again"
      );
    }
  };

  // Image click handler
  const handleClick = (event) => {
    localStorage.setItem("eventId", event.id);
    if (
      event.status === "active" ||
      (localStorage.getItem("isKJ") === "true" && event.status !== "finished")
    ) {
      navigate(`/EventDetails/${event.venueId}`);
    } else if (event.status === "finished") {
      navigate(`/venue/${event.id}/overview`);
    } else {
      toast.error("Make event live to enter the event");
    }
  };

  // Start live session
  const startLiveSession = async (venueId) => {
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.VENUE_STATUS(venueId), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status: "active" }),
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Failed to go live");
      }
      setLiveId(venueId);
      toast.success("Event is live now");
      navigate(`/EventDetails/${venueId}`);
    } catch (err) {
      console.error("Error setting event live:", err);
      toast.error("Failed to go live. Please try again.");
    }
  };

  // End live session
  const endLiveSession = async (venueId) => {
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.VENUE_STATUS(venueId), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status: "finished" }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Failed to end session");
      }
      setLiveId(null);
      toast.success("Event is finished now");
      fetchData(); // Refresh the events list
    } catch (err) {
      console.error("Error ending event:", err);
      toast.error("Failed to end session. Please try again.");
    }
  };

  const handleEnd = (venueId) => {
    setSelectedEventId(venueId);
    setShowEndConfirm(true);
  };

  // Sidebar navigation
  const handleSidebarNav = (page) => {
    setSidebarPage(page);
    switch (page) {
      case "home":
        navigate("/loggedinartist");
        break;
      case "calendar":
        navigate("/bookevent");
        break;
      case "qr":
        navigate("/me");
        break;
      case "profile":
        navigate("/artistprofile");
        break;
      default:
        break;
    }
  };

  // Updated handleAddEventSuccess function
  const handleAddEventSuccess = async () => {
    try {
      setSkip(0);
      setHasMore(true);
      await fetchEvents(true);
      window.location.reload();
    } catch (err) {
      console.error("Error refreshing events:", err);
      toast.error("Failed to refresh events list");
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const isBusinessUser = localStorage.getItem("isBusiness") === "true";
  const subStatus = (subscription?.status || "").toLowerCase();
  const hasActiveSub =
    isBusinessUser ||
    subStatus === "active" ||
    subStatus === "subscribed" ||
    subStatus === "authenticated";

  const handleAddEventClick = () => {
    if (hasActiveSub) {
      setShowAddEventModal(true);
    } else {
      setIsModalOpen(true);
    }
  };

  // Extend session
  const extendSession = async (venueId, timeValue, timeUnit) => {
    try {
      const token = localStorage.getItem("tringAuthToken");
      const event = events.find((v) => v.venueId === venueId);
      const currentEndTime = new Date(event.endAt);
      let newEndTime;

      if (timeUnit === "minutes") {
        newEndTime = new Date(currentEndTime.getTime() + timeValue * 60000);
      } else {
        newEndTime = new Date(
          currentEndTime.getTime() + timeValue * 60 * 60000
        );
      }

      const response = await fetchWithAuth(API_ENDPOINTS.VENUES(venueId), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          endAt: newEndTime.toISOString(),
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to extend venue: ${errorText}`);
      }

      fetchData(); // Refresh the events list
      setShowExtendModal(false);
      const timeText =
        timeUnit === "minutes"
          ? `${timeValue} minute${timeValue > 1 ? "s" : ""}`
          : `${timeValue} hour${timeValue > 1 ? "s" : ""}`;
      toast.success(`Session extended for ${timeText} successfully!`);
    } catch (err) {
      console.error("Error extending venue:", err);
      toast.error("Failed to extend session. Please try again.");
    }
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-white via-[#F8F4FB] to-[#F0EBF5] w-screen relative md:mt-0 mt-20">
      <MobileNBottomNav />
      <div className="fixed top-0 left-0 z-50 w-full px-2 py-3 flex items-center justify-between bg-logo backdrop-blur-sm lg:hidden">
        {/* Static Title and Logo */}
        <div className="flex items-center gap-2">
          <img alt="" src="/images/tringbox-logo.png" className="h-6" />
        </div>

        {/* Animated Toggle Button */}
        <motion.button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className="p-2 rounded-md"
          aria-label="Toggle sidebar"
        >
          {isSidebarOpen ? (
            <X size={24} className="text-white" />
          ) : (
            <Menu size={24} className="text-white" />
          )}
        </motion.button>
      </div>

      {/* Sidebar */}
      <div
        className={`fixed left-0 top-0 w-80 h-full z-40 transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          isSidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <Sidebar currentPage={sidebarPage} onPageChange={handleSidebarNav} />
      </div>
      <ToastContainer position="top-center" autoClose={3000} />

      {/* Main Content */}
      <main className="flex-1 min-h-screen lg:ml-80 w-full flex flex-col">
        {/* Header with Search */}
        <header className="bg-white/80 backdrop-blur-sm border-b border-purple-100 py-3 lg:py-4 sticky top-0 z-30 w-full px-3 sm:px-4 lg:px-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-1 lg:mb-1">
            {/* Profile Section */}
            <div className="flex items-center space-x-3 md:mb-4 mb-2 lg:mb-0">
              <img src={profile.avatar} className="h-14 w-14 rounded-full" />
              <div className="h-14 flex flex-col gap-0">
                <h1 className="text-lg lg:text-xl font-bold text-[#2D0A4A]">
                  Hello, {profile?.display_name || "User"}
                </h1>
                <p className="text-xs lg:text-sm text-[#4A4A4A]">
                  Ready to rock the stage?
                </p>
              </div>
            </div>

            {/* Search Section */}
            <div className="flex items-center space-x-2 lg:space-x-4  lg:mt-0">
              <div className="relative flex-1 lg:flex-none">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#4A4A4A]" />
                <input
                  type="text"
                  placeholder="Search events..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-12 py-2 w-full lg:w-80 rounded-xl border border-purple-200 bg-white/50 focus:outline-none focus:ring-2 focus:ring-logo focus:border-transparent text-sm"
                />
              </div>
            </div>
          </div>

          {/* Event Type Tabs */}
          <div className="flex space-x-2 lg:space-x-5 lg:px-2 overflow-x-auto scrollbar-hide md:mt-3 mt-2">
            {[
              { id: "all", label: "All Events" },
              { id: "live", label: "Live" },
              { id: "upcoming", label: "Upcoming" },
              { id: "ended", label: "Ended" },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveFilter(tab.id)}
                className={`
                px-3 lg:px-4 py-2 rounded-lg text-xs lg:text-sm font-bold transition-all whitespace-nowrap
                ${
                  activeFilter === tab.id
                    ? "bg-logo text-white"
                    : "text-[#4A4A4A] hover:bg-gray-100"
                }
              `}
              >
                {tab.label}
              </button>
            ))}
            <div className="flex-1" />
            <button
              className="hidden md:flex items-center space-x-2 bg-gradient-to-r from-logo to-[#6720A3] text-white px-4 py-2 md:px-6 md:py-3 rounded-xl font-medium shadow-lg w-full md:w-auto justify-center"
              onClick={handleAddEventClick}
            >
              <Plus className="h-4 w-4 md:h-5 md:w-5" />
              <span>Add Event</span>
            </button>
          </div>
        </header>

        {/* Events List - Updated Layout */}
        <section className="flex-1 p-3 sm:p-4 lg:p-6 w-full">
          <AnimatePresence>
            {displayed.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <img
                  src="/images/dj.svg"
                  alt="No events found"
                  className="w-64 h-64 opacity-70 bg text-center"
                />
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:gap-3 gap-2 lg:gap-6 w-full mb-20">
                {displayed.map((event, idx) => (
                  <motion.div
                    key={event.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                    transition={{ delay: idx * 0.05 }}
                    className="w-full h-full lg:max-h-40 sm:max-h-40 border border-neutral-600 relative bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                  >
                    {/* Event Header - Responsive layout */}
                    <div className="flex flex-col sm:flex-row w-full h-full">
                      {/* Event Image */}
                      <div className="relative w-full sm:w-1/3 h-full overflow-hidden cursor-pointer">
                        {/* Status Badge - Mobile Only */}
                        <div className="absolute top-2 right-2 z-10 sm:hidden">
                          <div
                            className={`
                            px-3 py-1 rounded-full text-xs font-medium
                            ${
                              event.status === "active"
                                ? "bg-green-500"
                                : event.status === "finished"
                                ? "bg-red-500"
                                : event.status === "paused"
                                ? "bg-yellow-400"
                                : "bg-logo"
                            }
                            text-white shadow-lg backdrop-blur-sm bg-opacity-90
                          `}
                          >
                            {event.status === "active" && "Live"}
                            {event.status === "finished" && "Ended"}
                            {event.status === "approved" && "Upcoming"}
                            {event.status === "paused" && "Paused"}
                          </div>
                        </div>
                        <div
                          className="w-full h-full"
                          onClick={() => handleClick(event)}
                        >
                          <img
                            src={
                              event.photoUrl ||
                              "https://via.placeholder.com/400x200"
                            }
                            alt={event.name}
                            className="w-full lg:h-40 sm:h-40 h-40 object-cover"
                          />
                        </div>
                        <div className="absolute top-2 left-2 z-10">
                          <div
                            className={`
                            px-3 py-1 rounded-full text-xs font-medium
                            bg-black/60
                            text-white shadow-lg backdrop-blur-sm bg-opacity-90
                          `}
                          >
                            {formatDate(event.startAt)}
                          </div>
                        </div>
                      </div>

                      {/* Event Details */}
                      <div className="sm:w-2/3 p-3 lg:p-4 flex flex-col justify-between relative">
                        <div>
                          <div
                            className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2"
                            onClick={() => handleClick(event)}
                          >
                            <h3 className="text-lg lg:text-xl font-bold text-gray-900 cursor-pointer">
                              {event.name}
                            </h3>
                            {/* Status Badge - Desktop Only */}
                            <div className="hidden sm:block">
                              <div
                                className={`
                                px-3 py-1 rounded-full text-xs font-medium self-start
                                ${
                                  event.status === "active"
                                    ? "bg-green-500"
                                    : event.status === "finished"
                                    ? "bg-red-500"
                                    : event.status === "paused"
                                    ? "bg-yellow-400"
                                    : "bg-logo"
                                }
                                text-white
                              `}
                              >
                                {event.status === "active" && "Live"}
                                {event.status === "finished" && "Ended"}
                                {event.status === "approved" && "Upcoming"}
                                {event.status === "paused" && "Paused"}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center text-xs lg:text-sm text-gray-500">
                            <MapPin className="h-3 w-3 lg:h-4 lg:w-4 mr-1 flex-shrink-0" />
                            <span className="truncate">
                              {event.venue?.address?.line1
                                ? `${event.venue.address.line1}${
                                    event.venue.address.city
                                      ? ", " + event.venue.address.city
                                      : ""
                                  }${
                                    event.venue.address.state
                                      ? ", " + event.venue.address.state
                                      : ""
                                  }`
                                : "No address"}
                            </span>
                          </div>
                        </div>

                        {/* Controls - Responsive positioning */}
                        <div className="flex flex-wrap w-full items-center gap-2 lg:gap-3 md:mt-4 mt-3 lg:mt-0 lg:right-4">
                          {/* Live Toggle Switch with label */}
                          {(event.status === "active" ||
                            event.status === "approved" ||
                            event.status === "paused") && (
                            <label className="inline-flex items-center cursor-pointer ">
                              <input
                                type="checkbox"
                                checked={event.status === "active"}
                                onChange={() => handleLiveToggle(event.venueId)}
                                className="sr-only peer"
                                disabled={event.status === "finished"}
                              />
                              <div
                                className={`
        w-10 h-5 lg:w-12 lg:h-6 rounded-full relative
        after:content-[''] after:absolute after:top-0.5 after:left-[2px]
        after:bg-white after:border after:border-gray-300 after:rounded-full after:h-4 after:w-4 lg:after:h-5 lg:after:w-5 after:transition-all
        peer-checked:after:translate-x-5 lg:peer-checked:after:translate-x-6
        peer-checked:bg-green-600 bg-gray-300
      `}
                              />
                              <span
                                className={`ml-2 text-xs lg:text-sm font-semibold ${
                                  event.status === "active"
                                    ? "text-green-600"
                                    : "text-gray-500"
                                }`}
                              >
                                {event.status === "active" ? "Live" : "Live"}
                              </span>
                            </label>
                          )}

                          {/* Pending breakdown chip for active event */}
                          {event.status === "active" && (
                            <div
                              onClick={() => handleClick(event)}
                              className="hidden sm:flex items-center gap-2 text-xs text-purple-900 bg-purple-50/90 hover:bg-purple-100 px-3 py-1.5 rounded-full border border-purple-200 cursor-pointer transition-colors shadow-2xs"
                              title="Pending requests breakdown: Requests, Accepted, Shoutouts"
                            >
                              <span className="font-semibold text-[#6720A4]">
                                {pendingCounts[event.venueId]?.requests ?? 0} Requests
                              </span>
                              <span className="text-purple-300">•</span>
                              <span className="font-semibold text-green-700">
                                {pendingCounts[event.venueId]?.accepted ?? 0} Accepted
                              </span>
                              <span className="text-purple-300">•</span>
                              <span className="font-semibold text-amber-700">
                                {pendingCounts[event.venueId]?.shoutouts ?? 0} Shoutouts
                              </span>
                            </div>
                          )}

                          <div className="flex-1" />
                          {/* Buttons */}
                          {event.status === "active" && (
                            <>
                              <button
                                type="button"
                                onClick={() => handleClick(event)}
                                className="px-3 lg:px-4 py-2 bg-purple-50 hover:bg-purple-100 border border-purple-200 text-[#6720A4] rounded-lg font-semibold flex items-center space-x-1.5 lg:space-x-2 transition-all text-xs lg:text-sm shadow-2xs hover:scale-[1.02] active:scale-[0.98]"
                                title="Click to view requests"
                              >
                                <div className="relative flex items-center justify-center">
                                  <Bell className="h-3.5 w-3.5 lg:h-4 lg:w-4 text-[#6720A4]" />
                                  {(pendingCounts[event.venueId]?.total || 0) > 0 && (
                                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-ping" />
                                  )}
                                </div>
                                <span>
                                  <strong className="font-bold text-[#6720A4]">
                                    {pendingCounts[event.venueId]?.total ?? 0}
                                  </strong>{" "}
                                  Pending Request{(pendingCounts[event.venueId]?.total === 1 ? "" : "s")}
                                </span>
                              </button>
                              <button
                                type="button"
                                onClick={() => handleEnd(event.venueId)}
                                className="px-3 lg:px-4 py-2 bg-red-500 text-white rounded-lg font-medium flex items-center space-x-1 lg:space-x-2 hover:bg-red-600 transition-colors text-xs lg:text-sm"
                              >
                                <LogOut className="h-3 w-3 lg:h-4 lg:w-4" />
                                <span>End</span>
                              </button>
                            </>
                          )}
                          {event.status === "approved" && (
                            <button
                              type="button"
                              className="px-3 lg:px-4 py-2 bg-gray-400 text-white rounded-lg font-medium flex items-center space-x-1 lg:space-x-2 text-xs lg:text-sm cursor-not-allowed"
                              disabled
                            >
                              <LogOut className="h-3 w-3 lg:h-4 lg:w-4" />
                              <span>End</span>
                            </button>
                          )}
                          {event.status === "finished" && (
                            <div className="flex items-center gap-2 w-full md:w-auto">
                              <button
                                type="button"
                                onClick={() =>
                                  navigate(`/venue/${event.id}/overview`)
                                }
                                className="px-3 lg:px-4 py-2 bg-logo text-white rounded-lg font-medium hover:bg-[#62187D] transition-colors text-xs lg:text-sm flex-1 md:flex-initial"
                              >
                                Summary
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  setSelectedVenueForStats(event.venueId || event.id)
                                }
                                className="px-3 lg:px-4 py-2 bg-purple-50 text-logo border border-purple-200 rounded-lg font-medium hover:bg-purple-100 transition-colors text-xs lg:text-sm flex items-center justify-center gap-1.5 flex-1 md:flex-initial"
                              >
                                <BarChart3 className="w-3.5 h-3.5" />
                                <span>Stats</span>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
                {isLoadingMore && (
                  <div className="flex items-center justify-center w-full py-4">
                    <div className="animate-pulse text-logo text-sm font-bold">
                      Loading more events...
                    </div>
                  </div>
                )}
                {hasMore && (
                  <div className="flex justify-center mt-6 col-span-full">
                    <button
                      onClick={fetchMoreEvents}
                      disabled={isLoadingMore}
                      className="bg-logo text-white px-6 py-3 rounded-lg font-medium hover:bg-purple-800 transition-colors disabled:opacity-50"
                    >
                      {isLoadingMore ? "Loading..." : "Show More"}
                    </button>
                  </div>
                )}
              </div>
            )}
          </AnimatePresence>
        </section>

        {/* Floating Add Button - Responsive positioning */}
        <motion.button
          type="button" // 👈 fixes iOS multi-tap issue
          className="fixed bottom-20 right-4 md:hidden block lg:bottom-6 lg:right-6 bg-gradient-to-r from-logo to-[#6720A3] text-white p-3 lg:p-4 rounded-full shadow-lg z-50"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={handleAddEventClick}
        >
          <Plus className="h-5 w-5 lg:h-6 lg:w-6" />
        </motion.button>

        <Modal
          isOpen={showAddEventModal}
          onClose={() => setShowAddEventModal(false)}
        >
          <AddEventFormFields
            onSuccess={handleAddEventSuccess}
            setAddEvent={setShowAddEventModal}
          />
        </Modal>
        <YourSubscriptionModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSuccess={async () => {
            try {
              const subGet = await fetchWithAuth(
                API_ENDPOINTS.PAYTM_SUBSCRIPTION,
                { method: "GET" }
              );
              if (subGet.ok) {
                const subData = await subGet.json();
                setSubscription(subData);
              }
            } catch (err) {
              console.error("Error refreshing subscription:", err);
            }
            setIsModalOpen(false);
            setShowAddEventModal(true);
          }}
        />

        {/* End Confirmation Modal */}
        <Modal isOpen={showEndConfirm} onClose={() => setShowEndConfirm(false)}>
          <div className="p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              End Session
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to end this session? This action cannot be
              undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowEndConfirm(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  endLiveSession(selectedEventId);
                  setShowEndConfirm(false);
                }}
                className="px-4 py-2 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600"
              >
                End Session
              </button>
            </div>
          </div>
        </Modal>

        {/* Go Live Confirmation Modal */}
        {showLiveConfirm && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
              className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ type: "spring", duration: 0.5 }}
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Play className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Start Live Session
                </h3>
                <p className="text-gray-600 mb-2">
                  Are you sure you want to start this live session?
                </p>
                <div className="bg-blue-50 rounded-lg md:p-4 p-2 text-sm text-blue-800">
                  <ul className="list-disc md:list-inside md:space-y-1 space-y-2 md:text-center text-start">
                    <li>Users will be able to make song requests</li>
                    <li>You can manage requests and playlists</li>
                    <li>Session can be paused or extended if needed</li>
                  </ul>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  className="flex-1 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors"
                  onClick={() => setShowLiveConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="flex-1 py-3 px-4 bg-green-500 hover:bg-green-600 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
                  onClick={() => {
                    startLiveSession(selectedEventForLive);
                    setShowLiveConfirm(false);
                  }}
                >
                  <Play className="w-4 h-4" />
                  Start Live
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {/* End Live Confirmation Modal */}
        {showEndLiveConfirm && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
              className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ type: "spring", duration: 0.5 }}
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <LogOut className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  End Live Session
                </h3>
                <p className="text-gray-600 mb-2">
                  Are you sure you want to end this live session?
                </p>
                <div className="bg-red-50 rounded-lg md:p-4 p-2 text-sm text-red-800 text-left">
                  <ul className="list-disc list-inside space-y-1">
                    <li>All current requests will be lost</li>
                    <li>Users won't be able to make new requests</li>
                    <li>Session cannot be resumed once ended</li>
                  </ul>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  className="flex-1 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors"
                  onClick={() => setShowEndLiveConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="flex-1 py-3 px-4 bg-red-500 hover:bg-red-600 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
                  onClick={() => {
                    endLiveSession(selectedEventForLive);
                    setShowEndLiveConfirm(false);
                  }}
                >
                  <LogOut className="w-4 h-4" />
                  End Session
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Extend Session Modal */}
        {showExtendModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
              className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ type: "spring", duration: 0.5 }}
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Plus className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Extend Session
                </h3>
                <p className="text-gray-600 mb-6">
                  Choose how long you want to extend the session:
                </p>

                <div className="grid grid-cols-2 gap-3 mb-6">
                  {timeOptions.map((option) => (
                    <button
                      key={`${option.value}${option.unit}`}
                      onClick={() => {
                        setExtensionTime(option.value);
                        setExtensionUnit(option.unit);
                      }}
                      className={`
                      py-4 px-6 rounded-xl font-medium transition-all
                      ${
                        extensionTime === option.value &&
                        extensionUnit === option.unit
                          ? "bg-blue-500 text-white ring-2 ring-blue-500 ring-offset-2"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }
                    `}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>

                <div className="bg-blue-50 rounded-lg p-4 text-sm text-blue-800">
                  <p className="font-medium mb-1">Selected extension:</p>
                  <p className="text-lg font-bold">
                    {extensionTime}{" "}
                    {extensionUnit === "minutes"
                      ? "minutes"
                      : extensionTime === 1
                      ? "hour"
                      : "hours"}
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  className="flex-1 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors"
                  onClick={() => setShowExtendModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="flex-1 py-3 px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
                  onClick={() => {
                    extendSession(
                      selectedEventForLive,
                      extensionTime,
                      extensionUnit
                    );
                  }}
                >
                  <Plus className="w-4 h-4" />
                  Extend Session
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Session Stats Modal */}
        <SessionStatsModal
          open={Boolean(selectedVenueForStats)}
          onClose={() => setSelectedVenueForStats(null)}
          venueId={selectedVenueForStats}
        />
      </main>
    </div>
  );
}
